import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Loader2,
  CheckCircle2,
  Circle,
  AlertTriangle,
  ArrowLeft,
  RefreshCcw,
  ArrowRight,
  Clock,
} from "lucide-react";
import { lessonPlanApi, AILessonGenerationStatus } from "../api/lessonPlan";
import { useToast } from "../contexts/ToastContext";
import AiSourceControl, { AiAnsweredBy } from "./ai/AiSourceControl";
import HandoffControl from "./ai/HandoffControl";
import { answeredByText } from "../api/myAi";

interface Props {
  entryId: number;
  lessonId?: number;
  weekLabel?: string;
  topic?: string;
  // The exact calendar day (YYYY-MM-DD) this plan is for — the day the
  // teacher clicked, not just the scheme week it falls in.
  lessonDate?: string;
  onComplete: () => void;
  onCancel: () => void;
}

const STEPS: { key: AILessonGenerationStatus["status"]; label: string; description: string }[] = [
  { key: "loading", label: "Loading week context", description: "Reading this week's scheme of work details" },
  { key: "analyzing", label: "Analyzing with AI", description: "Gemini is planning the lesson as an experienced teacher would" },
  { key: "structuring", label: "Structuring lesson plan", description: "Mapping outcomes, sections, and assessment" },
  { key: "saving", label: "Saving to database", description: "Writing the lesson plan" },
];

const POLL_INTERVAL_MS = 1200;

const LessonPlanAIGenerate: React.FC<Props> = ({ entryId, lessonId, weekLabel, topic, lessonDate, onComplete, onCancel }) => {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobStatus, setJobStatus] = useState<AILessonGenerationStatus | null>(null);
  const [sessionHours, setSessionHours] = useState("2");
  const [customPrompt, setCustomPrompt] = useState("");
  const [aiRoute, setAiRoute] = useState("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const pollStatus = (jobId: string) => {
    pollRef.current = setInterval(async () => {
      try {
        const resp = await lessonPlanApi.getAIGenerateStatus(jobId);
        const status = resp.data.data;
        setJobStatus(status);

        if (status.status === "done") {
          if (pollRef.current) clearInterval(pollRef.current);
          const via = answeredByText(status.ai);
          showToast(`Lesson plan generated by AI!${via && via.tone !== "school" ? ` ${via.text}` : ""}`, via?.tone === "fallback" ? "warning" : "success");
          // A little longer when there's a "via My AI" or fallback note to read.
          setTimeout(() => onComplete(), via && via.tone !== "school" ? 2500 : 900);
        } else if (status.status === "error") {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch (err) {
        if (pollRef.current) clearInterval(pollRef.current);
        setJobStatus({
          status: "error",
          stepIndex: 0,
          totalSteps: 4,
          message: "Lost connection while checking progress",
          error: "Lost connection while checking progress",
        });
      }
    }, POLL_INTERVAL_MS);
  };

  const validHours = () => {
    const hours = parseFloat(sessionHours);
    if (isNaN(hours) || hours <= 0 || hours > 12) {
      showToast("Enter a valid session length between 0 and 12 hours", "error");
      return false;
    }
    return true;
  };

  const handleGenerate = async () => {
    if (!validHours()) return;
    const hours = parseFloat(sessionHours);

    setIsSubmitting(true);
    setJobStatus({ status: "loading", stepIndex: 1, totalSteps: 4, message: "Loading week context..." });

    try {
      const resp = await lessonPlanApi.startAIGenerate(
        entryId,
        hours,
        lessonId,
        lessonDate,
        customPrompt.trim() || undefined,
        aiRoute,
      );
      const { jobId } = resp.data.data;
      pollStatus(jobId);
    } catch (error: any) {
      setJobStatus({
        status: "error",
        stepIndex: 0,
        totalSteps: 4,
        message: "Could not start AI generation",
        error: error.response?.data?.message || "Could not start AI generation",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => setJobStatus(null);

  const isProcessing = !!jobStatus && !["done", "error"].includes(jobStatus.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className="w-full max-w-4xl mx-auto"
    >
      <div className="relative overflow-hidden p-6 sm:p-10 bg-white dark:bg-gray-900 border-2 border-violet-100 dark:border-violet-900/40 rounded-3xl">
        <div className="absolute -top-16 -right-16 w-40 h-40 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-indigo-500/10 rounded-full blur-3xl" />

        <AnimatePresence mode="wait">
          {!jobStatus && (
            <motion.div
              key="start"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-8 lg:gap-12 items-center"
            >
              {/* Branding / context — left column on wide screens, stacked
                  and centered on mobile so it reads top-to-bottom naturally. */}
              <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-violet-500/25">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1.5">
                  Generate Lesson Plan with AI
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                  AI will act as an experienced teacher and build a full lesson plan from{" "}
                  {weekLabel || "this week"}'s scheme of work entry — no upload needed.
                </p>
                {topic && (
                  <p className="text-xs text-violet-600 dark:text-violet-400 font-medium mt-3 bg-violet-50 dark:bg-violet-900/20 px-3 py-1.5 rounded-full">
                    "{topic}"
                  </p>
                )}
              </div>

              {/* The actual form — right column, full-width fields instead
                  of a narrow centered stack that wasted the modal's space. */}
              <div className="w-full text-left">
                <div className="mb-5">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Session Length (hours)
                  </label>
                  <input
                    type="number"
                    min={0.25}
                    max={12}
                    step={0.25}
                    value={sessionHours}
                    onChange={(e) => setSessionHours(e.target.value)}
                    className="w-full px-4 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900/80 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 hover:border-gray-300 dark:hover:border-gray-500 transition-all"
                  />
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                    AI will distribute this time across the learning outcomes and lesson sections for you.
                  </p>
                </div>
                <div className="mb-6">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
                    Custom instructions
                    <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder='e.g. "Focus more on group activities" or "This class struggles with fractions — add a recap"'
                    className="w-full px-4 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900/80 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500 hover:border-gray-300 dark:hover:border-gray-500 transition-all"
                  />
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                    Tell the AI anything specific for this lesson — it's applied on top of the scheme of work, not instead of it.
                  </p>
                </div>
                <AiSourceControl task="author" value={aiRoute} onChange={setAiRoute} className="mb-4" />
                <HandoffControl
                  className="mb-4"
                  disabled={isSubmitting}
                  beforeRun={validHours}
                  run={(extra) =>
                    lessonPlanApi
                      .startAIGenerate(entryId, parseFloat(sessionHours), lessonId, lessonDate, customPrompt.trim() || undefined, undefined, extra)
                      .then((r) => r.data)
                  }
                  onImported={(body) => {
                    // The pasted plan is already saved: the first poll reads "done".
                    setJobStatus({ status: "saving", stepIndex: 4, totalSteps: 4, message: "Saving lesson plan..." });
                    pollStatus(body.data.jobId);
                  }}
                />
                <button
                  onClick={handleGenerate}
                  disabled={isSubmitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-full transition-colors shadow-sm shadow-violet-600/25 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {isSubmitting ? "Starting..." : "Generate Lesson Plan"}
                </button>
                <button
                  onClick={onCancel}
                  className="mt-4 inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to options
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress / done / error states stay centered — they're transient
            status views, not a form, so a single focused column still reads
            best even inside the now-wider card. */}
        <div className="relative flex flex-col items-center text-center max-w-md mx-auto">
          <AnimatePresence mode="wait">
            {jobStatus && isProcessing && (
              <motion.div
                key="progress"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full text-left"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wide">
                    Step {jobStatus.stepIndex} of {jobStatus.totalSteps}
                  </span>
                  <span className="text-xs text-gray-400">
                    {STEPS.length - jobStatus.stepIndex} step
                    {STEPS.length - jobStatus.stepIndex === 1 ? "" : "s"} remaining
                  </span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden mb-6">
                  <motion.div
                    className="h-full bg-gradient-to-r from-violet-500 to-indigo-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${(jobStatus.stepIndex / jobStatus.totalSteps) * 100}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
                <div className="flex flex-col gap-4">
                  {STEPS.map((step, idx) => {
                    const stepNum = idx + 1;
                    const isDone = stepNum < jobStatus.stepIndex ||
                      (stepNum === jobStatus.stepIndex && jobStatus.status !== step.key);
                    const isActive = stepNum === jobStatus.stepIndex && jobStatus.status === step.key;
                    return (
                      <div key={step.key} className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {isDone ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          ) : isActive ? (
                            <Loader2 className="w-5 h-5 text-violet-600 dark:text-violet-400 animate-spin" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-300 dark:text-gray-700" />
                          )}
                        </div>
                        <div>
                          <p
                            className={`text-sm font-semibold ${
                              isActive
                                ? "text-violet-700 dark:text-violet-300"
                                : isDone
                                  ? "text-gray-700 dark:text-gray-300"
                                  : "text-gray-400 dark:text-gray-600"
                            }`}
                          >
                            {step.label}
                          </p>
                          {isActive && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {step.description}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {jobStatus?.status === "done" && (
              <motion.div
                key="done"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full flex flex-col items-center py-4"
              >
                <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Lesson plan ready!
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                  Opening your lesson plan <ArrowRight className="w-3 h-3" />
                </p>
                <AiAnsweredBy ai={jobStatus.ai} className="mt-2" />
              </motion.div>
            )}

            {jobStatus?.status === "error" && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="w-full flex flex-col items-center py-2"
              >
                <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-3">
                  <AlertTriangle className="w-7 h-7 text-red-500" />
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  Generation failed
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs mb-4">
                  {jobStatus.error || jobStatus.message}
                </p>
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-full transition-colors"
                >
                  <RefreshCcw className="w-4 h-4" />
                  Try Again
                </button>
                <button
                  onClick={onCancel}
                  className="mt-4 inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to options
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default LessonPlanAIGenerate;
